import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

import { useToast } from '../../../components/ui/Toast';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

const AddLeadModal = ({ isOpen, onClose, onLeadAdded }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    source: 'manual',
    status: 'new',
    lead_score: 50
  });
  const toast = useToast();

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' }
  ];

  const sourceOptions = [
    { value: 'manual', label: 'Manual Entry' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'email_campaign', label: 'Email Campaign' },
    { value: 'cold_outreach', label: 'Cold Outreach' },
    { value: 'event', label: 'Event' },
    { value: 'other', label: 'Other' }
  ];

  const validateField = (field, value) => {
    switch (field) {
      case 'first_name':
        if (!value?.trim()) return 'First name is required';
        if (value?.trim()?.length < 2) return 'Must be at least 2 characters';
        return '';
      case 'email':
        if (value?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Please enter a valid email';
        return '';
      case 'phone':
        if (value?.trim() && !/^[\+]?[0-9\s\-\(\)]{7,}$/?.test(value?.trim())) return 'Please enter a valid phone number';
        return '';
      case 'lead_score': {
        const n = parseInt(value);
        if (isNaN(n) || n < 0 || n > 100) return 'Score must be between 0 and 100';
        return '';
      }
      default:
        return '';
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
    if (error) setError('');
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFieldErrors(prev => ({ ...prev, [field]: validateField(field, formData?.[field]) }));
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      job_title: '',
      source: 'manual',
      status: 'new',
      lead_score: 50
    });
    setFieldErrors({});
    setTouched({});
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate required fields
    const firstNameErr = validateField('first_name', formData?.first_name);
    const emailErr = validateField('email', formData?.email);
    const phoneErr = validateField('phone', formData?.phone);
    const scoreErr = validateField('lead_score', formData?.lead_score);

    // Need at least name or email
    if (!formData?.first_name?.trim() && !formData?.email?.trim()) {
      setError('Please enter at least a first name or email address');
      setTouched({ first_name: true, email: true });
      setFieldErrors({ first_name: 'First name is required if no email provided', email: '' });
      return;
    }

    const newErrors = {};
    if (firstNameErr && formData?.first_name) newErrors.first_name = firstNameErr;
    if (emailErr) newErrors.email = emailErr;
    if (phoneErr) newErrors.phone = phoneErr;
    if (scoreErr) newErrors.lead_score = scoreErr;

    setTouched({ first_name: true, email: true, phone: true, lead_score: true });
    setFieldErrors(newErrors);

    if (Object.keys(newErrors)?.length > 0) return;

    if (!user?.id) {
      setError('You must be logged in to add leads');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use SECURITY DEFINER RPC to ensure user_profiles row exists (bypasses RLS)
      // This prevents the funnel_leads_user_id_fkey FK constraint violation
      const { error: rpcError } = await supabase?.rpc('ensure_user_profile_exists', {
        p_user_id: user?.id,
        p_email: user?.email || ''
      });

      if (rpcError) {
        console.warn('ensure_user_profile_exists RPC error:', rpcError?.message);
        // Fallback: try direct upsert
        const { error: upsertError } = await supabase
          ?.from('user_profiles')
          ?.upsert({
            id: user?.id,
            user_id: user?.id,
            email: user?.email || '',
            updated_at: new Date()?.toISOString()
          }, { onConflict: 'id' });
        if (upsertError) {
          console.warn('Fallback upsert error:', upsertError?.message);
          // Don't abort — attempt the insert anyway; if FK fails, show proper error
        }
      }

      const { data, error: insertError } = await supabase
        ?.from('funnel_leads')
        ?.insert([{
          user_id: user?.id,
          first_name: formData?.first_name || '',
          last_name: formData?.last_name || '',
          email: formData?.email || '',
          phone: formData?.phone || '',
          company: formData?.company || '',
          job_title: formData?.job_title || '',
          source: formData?.source || 'manual',
          lead_score: parseInt(formData?.lead_score) || 50,
          is_converted: false,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();

      if (insertError) {
        if (insertError?.message?.includes('foreign key') || insertError?.code === '23503') {
          setError('Your account profile is not fully set up. Please sign out and sign back in, then try again.');
        } else {
          setError(insertError?.message || 'Failed to add lead');
        }
        return;
      }

      toast?.success(
        `${formData?.first_name || formData?.email || 'Lead'} has been added to your pipeline.`,
        'Lead Added Successfully'
      );

      if (onLeadAdded) onLeadAdded(data);
      resetForm();
      onClose();
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const FieldError = ({ field }) => fieldErrors?.[field] && touched?.[field] ? (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="w-3 h-3 flex-shrink-0" />
      {fieldErrors?.[field]}
    </p>
  ) : null;

  const inputClass = (field) => {
    const hasError = fieldErrors?.[field] && touched?.[field];
    const isValid = touched?.[field] && !fieldErrors?.[field] && formData?.[field];
    return hasError ? 'border-red-400 bg-red-50 focus:ring-red-200 focus:border-red-400' : isValid ?'border-green-400 bg-green-50' :'';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Add New Lead</h2>
              <p className="text-sm text-muted-foreground">Manually add a lead to your pipeline</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle size={16} className="text-red-500 shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">First Name *</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="John"
                  value={formData?.first_name}
                  onChange={(e) => handleChange('first_name', e?.target?.value)}
                  onBlur={() => handleBlur('first_name')}
                  className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors pr-8 ${inputClass('first_name')}`}
                />
                {touched?.first_name && !fieldErrors?.first_name && formData?.first_name && (
                  <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                )}
              </div>
              <FieldError field="first_name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Last Name</label>
              <input
                type="text"
                placeholder="Doe"
                value={formData?.last_name}
                onChange={(e) => handleChange('last_name', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
            <div className="relative">
              <input
                type="email"
                placeholder="john@company.com"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                onBlur={() => handleBlur('email')}
                className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors pr-8 ${inputClass('email')}`}
              />
              {touched?.email && !fieldErrors?.email && formData?.email && (
                <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            <FieldError field="email" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData?.phone}
                  onChange={(e) => handleChange('phone', e?.target?.value)}
                  onBlur={() => handleBlur('phone')}
                  className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors pr-8 ${inputClass('phone')}`}
                />
                {touched?.phone && !fieldErrors?.phone && formData?.phone && (
                  <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                )}
              </div>
              <FieldError field="phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Company</label>
              <input
                type="text"
                placeholder="Acme Corp"
                value={formData?.company}
                onChange={(e) => handleChange('company', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Job Title</label>
              <input
                type="text"
                placeholder="CEO"
                value={formData?.job_title}
                onChange={(e) => handleChange('job_title', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Lead Score</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData?.lead_score}
                  onChange={(e) => handleChange('lead_score', e?.target?.value)}
                  onBlur={() => handleBlur('lead_score')}
                  className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${inputClass('lead_score')}`}
                />
              </div>
              <FieldError field="lead_score" />
            </div>
          </div>

          <Select
            label="Lead Source"
            options={sourceOptions}
            value={formData?.source}
            onChange={(value) => handleChange('source', value)}
          />

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  Adding Lead...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="UserPlus" size={16} />
                  Add Lead
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLeadModal;
